export enum ErrorType {
  RESERVED_KEYWORD = 'RESERVED_KEYWORD',
  LENGTH = 'LENGTH',
  START_WITH_DIGIT = 'START_WITH_DIGIT',
  INVALID_CHARACTERS = 'INVALID_CHARACTERS',
  EMPTY = 'EMPTY',
}

export const ERROR_MESSAGES = {
  [ErrorType.RESERVED_KEYWORD]: 'Cannot be a reserved SQL keyword.',
  [ErrorType.LENGTH]: 'Must be up to 255 characters.',
  [ErrorType.START_WITH_DIGIT]: 'Cannot start with a digit.',
  [ErrorType.INVALID_CHARACTERS]: 'Must contain only letters, numbers, and underscores.',
  [ErrorType.EMPTY]: 'Table Name cannot be empty.',
}

const RESERVED_KEYWORD = new Set([
  'SELECT',
  'FROM',
  'WHERE',
  'INSERT',
  'UPDATE',
  'DELETE',
  'CREATE',
  'DROP',
  'ALTER',
  'TABLE',
  'VIEW',
  'INDEX',
  'TRIGGER',
  'GRANT',
  'REVOKE',
  'COMMIT',
  'ROLLBACK',
  'SAVEPOINT',
  'CURSOR',
  'PROCEDURE',
  'FUNCTION',
  'PACKAGE',
  'LOOP',
  'END',
  'IF',
  'THEN',
  'ELSE',
  'FOR',
  'WHILE',
  'BEGIN',
  'EXCEPTION',
])

interface ValidationError {
  type: ErrorType
}

/**
 * This function validates a Snowflake Outbound table name field and returns a
 * sanitized version of the name along with any errors that were found.
 *
 * @param name string
 * @returns
 * - sanitized: string
 * - errors: ValidationError[]
 *
 * @example
 * validateTableName('my_table') => { sanitized: 'my_table', errors: [] }
 * validateTableName('"My Table"') => { sanitized: '"My Table"', errors: [] }
 * validateTableName('SELECT') => { sanitized: '_SELECT', errors: [{ type: 'RESERVED_KEYWORD' }] }
 * validateTableName('123_table') => { sanitized: '_123_table', errors: [{ type: 'START_WITH_DIGIT' }] }
 *
 */
export function validateTableName(name = '') {
  const errors: ValidationError[] = []
  let sanitized = name

  if (name.length === 0) {
    errors.push({
      type: ErrorType.EMPTY,
    })
  }

  sanitized = sanitized.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '')

  // Check for special characters
  if (/[^a-zA-Z0-9_]/.test(name)) {
    errors.push({
      type: ErrorType.INVALID_CHARACTERS,
    })
  }

  // Check if the name starts with a digit
  if (/^\d/.test(name)) {
    errors.push({
      type: ErrorType.START_WITH_DIGIT,
    })

    sanitized = `_${sanitized}`
  }

  // Check if the name is a reserved keyword
  if (RESERVED_KEYWORD.has(name.toUpperCase())) {
    errors.push({
      type: ErrorType.RESERVED_KEYWORD,
    })

    sanitized = `_${sanitized}`
  }

  // Check if the name is empty or exceeds the length limit
  if (name.length > 255) {
    errors.push({
      type: ErrorType.LENGTH,
    })

    sanitized = sanitized.substring(0, 255)
  }

  return {
    sanitized,
    errors,
  }
}
